import { GameState } from "./game-state";
import { Ball } from "./ball";
import { HitResultEnum } from "../types/hit-result-enum";
import { Game } from "./game";
import { BallTypeEnum } from "../types/ball-type-enum";
import { Vector2 } from "../math/vector2";
import { GameConfig } from "../config/game-config";
import { IBallConfig } from "../types/game-config-type";
import gameApplication from "./game-app";
import { BallsFactory } from "./balls-factory";
import { store } from "../stores";
import { MutationType } from "../stores/mutation-types";
import { isInsideCircle } from "../math/helper";

const balls: IBallConfig = GameConfig.ball;

enum GameStage {
	FirstStage, SecondStage
};
class Foul {
	public message: String;
	public pts: number;

	constructor(message: String, pts: number) {
		this.message = message;
		this.pts = pts;
	}
};
export class GameSnooker extends Game {

	private static RED_BALL_ID: number = 1;
	
	private unknownIcon: string = 'default.svg';
	private colorIcon: string = 'colors.svg';

	private blackBall: Ball;
	private pinkBall: Ball;
	private blueBall: Ball;
	private greenBall: Ball;
	private brownBall: Ball;
	private yellowBall: Ball;

	// -1 for any colour, 1 for red, position in "balls" array for colour
	private currentBallId: number = 1; 

	private isSixRed: boolean;
	private reds: number = 15;

	protected stage: GameStage = GameStage.FirstStage;

	private slots = new Array(6);

	private isEnablePassback: boolean = false;

	constructor(isSixRed: boolean) {
		super();
		this.isSixRed = isSixRed;
		this.initGame();
	}

	/**
	 * This initilises the game. 
	 */
	public initGame(): void {
		this.rackBalls();

		// initilise the game state
		this.gameState = new GameState();
		this.gameState.setupCueBall = true;

		this.slots[0] = new Vector2(GameConfig.yellowBallPosition.x, GameConfig.yellowBallPosition.y);
		this.slots[1] = new Vector2(GameConfig.brownBallPosition.x, GameConfig.brownBallPosition.y);
		this.slots[2] = new Vector2(GameConfig.greenBallPosition.x, GameConfig.greenBallPosition.y);
		this.slots[3] = new Vector2(GameConfig.blueBallPosition.x, GameConfig.blueBallPosition.y);
		this.slots[4] = new Vector2(GameConfig.pinkBallPosition.x, GameConfig.pinkBallPosition.y);
		this.slots[5] = new Vector2(GameConfig.blackBallPosition.x, GameConfig.blackBallPosition.y);

		// ensure these initial values are cleared
		this.isSecondStage = false;
		this.currentBallPoints = -1;

		this.startGame()
	}

	private rackBalls(): void {
		this.gameBalls = BallsFactory.createSnookerBalls(this.isSixRed);

		this.reds = (this.isSixRed == true ? 6 : 15);

		this.cueBall = this.gameBalls[0];
		this.cueBall.ballPoints = 0;
		this.cueBall.ballIcon = 'white.svg';
		
		this.yellowBall = this.gameBalls[this.reds + 1];
		this.yellowBall.ballPoints = 2;
		this.yellowBall.ballIcon = 'yellow.svg';

		this.brownBall = this.gameBalls[this.reds + 2];
		this.brownBall.ballPoints = 3;
		this.brownBall.ballIcon = 'brown.svg';

		this.greenBall = this.gameBalls[this.reds + 3];
		this.greenBall.ballPoints = 4;
		this.greenBall.ballIcon = 'green.svg';

		this.blueBall = this.gameBalls[this.reds + 4];
		this.blueBall.ballPoints = 5;
		this.blueBall.ballIcon = 'blue.svg';

		this.pinkBall = this.gameBalls[this.reds + 5];
		this.pinkBall.ballPoints = 6;
    	this.pinkBall.ballIcon = 'pink.svg';

		this.blackBall = this.gameBalls[this.reds + 6];
		this.blackBall.ballPoints = 7;
    	this.blackBall.ballIcon = 'black.svg';	
	}

	protected processEvents(): HitResultEnum {
		let pts: number = 0;
		let result: HitResultEnum = HitResultEnum.OK;
		let fouls: Foul[] = new Array();
		let isCueBallScratched: boolean = false;
		let coloredBallsPocketed: number = 0;
		
		this.isEnablePassback = false;

		/** Check for fouls */
		if (!this.gameState.ballBallCollision) {
			fouls.push(new Foul(`Player ${this.currentTurnPlayer.playerId} hit no ball (foul, ${GameSnooker.FOUL_POINTS} points).`, GameSnooker.FOUL_POINTS));
		}
		else {
			let firstHittedBallPoints: number = this.gameState.firstCollidedBall.ballPoints;

			if (this.currentBallId == GameSnooker.RED_BALL_ID) { // ball is on red
				if (firstHittedBallPoints != GameSnooker.RED_BALL_ID) {
					// this is a foul - cue ball must first have contact with a red ball
					let pt: number = firstHittedBallPoints > GameSnooker.FOUL_POINTS ? firstHittedBallPoints : GameSnooker.FOUL_POINTS;
					fouls.push(new Foul(`Player ${this.currentTurnPlayer.playerId} hit wrong ball (foul, ${pt} points).`, pt));
				}

				// if current ball is red you can pot only red balls or it's a foul
				for (let b of this.gameState.pocketedBalls) {
					if (b.ballPoints !== GameSnooker.RED_BALL_ID) {
						let pt: number = b.ballPoints > GameSnooker.FOUL_POINTS ? b.ballPoints : GameSnooker.FOUL_POINTS;
						fouls.push(new Foul(`Player ${this.currentTurnPlayer.playerId} potted wrong ball (foul, ${pt} points).`, pt));
					}
				}
			}
			else { // ball is on colour, only colour balls are valid
				let legalColoredBallPoints: number = (this.stage == GameStage.FirstStage) ? firstHittedBallPoints : this.balls[this.currentBallId].ballPoints;
	
				// this is foul - cue ball must first have contact with a legal coloured ball
				if (firstHittedBallPoints === GameSnooker.RED_BALL_ID || firstHittedBallPoints !== legalColoredBallPoints) {
					let pt: number = firstHittedBallPoints > GameSnooker.FOUL_POINTS ? firstHittedBallPoints : GameSnooker.FOUL_POINTS;
					fouls.push(new Foul(`Player ${this.currentTurnPlayer.playerId} hit wrong ball (foul, ${pt} points).`, pt));
				}

				// find the amount of coloured balls pocketed
				for (let b of this.gameState.pocketedBalls) {
					if (b.ballPoints > GameSnooker.RED_BALL_ID) {
						coloredBallsPocketed++;
					}
				}

				this.coloredBallsPocketed = coloredBallsPocketed;

				// only one coloured ball can be potted when on a colour
				if (coloredBallsPocketed > 1) {
					for (let b of this.gameState.pocketedBalls) {
						let pt: number = b.ballPoints > GameSnooker.FOUL_POINTS ? b.ballPoints : GameSnooker.FOUL_POINTS;
						fouls.push(new Foul(`Player ${this.currentTurnPlayer.playerId} potted more than one colored ball (foul, ${pt} points).`, pt));
					}
				} // ensure that no reds or illegal coloured balls are pocketed
				else if (coloredBallsPocketed == 1) {
					for (let b of this.gameState.pocketedBalls) {
						if (b.ballPoints === GameSnooker.RED_BALL_ID || b.ballPoints !== legalColoredBallPoints) {
							let pt: number = b.ballPoints > GameSnooker.FOUL_POINTS ? b.ballPoints : GameSnooker.FOUL_POINTS;
							fouls.push(new Foul(`Player ${this.currentTurnPlayer.playerId} potted wrong ball (foul, ${pt} points).`, pt));
						}
					}
				}
			}
		}

		/** Check if the cue ball was scratched */
		for (let b of this.gameState.pocketedBalls) {
			if (b.type == BallTypeEnum.Cue) {
				fouls.push(new Foul(`Player ${this.currentTurnPlayer.playerId} scratched cue ball (foul, ${GameSnooker.FOUL_POINTS} points).`, GameSnooker.FOUL_POINTS));
				isCueBallScratched = true;
				this.isFoul = true;
				break;
			}
		}
		
		/** Finalize the points for the turn */
		if (fouls.length > 0) {
			let f = fouls.reduce((prev, current) => {
				return (prev.pts > current.pts) ? prev : current
			}); // get foul with highest value

			result = HitResultEnum.PLAYER_CHANGE;

			if (!isCueBallScratched) {
				this.isEnablePassback = true;
			}
			
			store.commit(MutationType.GAME_MESSAGE, f.message);

			// add foul penalty points to the opponents score
			this.scores[this.nextTurnPlayerIndex] += f.pts;
		}
		else {
			// no fouls so count points
			for (let b of this.gameState.pocketedBalls) {
				pts += b.ballPoints; // one point for each red ball
			}

			if (pts == 0) { // no potted balls - switch to opponent
				result = HitResultEnum.PLAYER_CHANGE;
			}
			else {
				result = HitResultEnum.OK; // there are some points
				// if it was proper hit then increment player score value
				this.scores[this.currentPlayerIndex] += pts;
			}
		}

		/** Respot balls logic */
		if (this.stage === GameStage.FirstStage) {
			this.respotBalls(2); // re-spot all potted coloured balls
		}
		else if (this.stage === GameStage.SecondStage) {
			if (coloredBallsPocketed > 1) {
				// re-spot all colour balls starting from the current ball
				this.respotBalls(this.currentBallId - 5);
			}
			else {
				// re-spot all colour balls starting from the next current ball
				this.respotBalls((this.currentBallId - 5) + 1);
			}
		}

		// Detect game stage change moment.
		// Change game state after all red balls are potted.
		if (this.stage == GameStage.FirstStage) {
			if (this.hasNoReds() && (this.currentBallId == -1 || fouls.length > 0)) {
				this.changeGameStage();
			}
		}

		// Check if there are any balls left on the table.
		let noBalls: boolean = true;
		for (let i: number = 1; i < this.balls.length; i++) {
			let b: Ball = this.balls[i];
			if (b.onTable) {
				noBalls = false;
				break;
			}
		}

		this.hasNoBalls = noBalls;

		if (noBalls) {
			if (this.scores[0] == this.scores[1]) {
				// if players points are equal, re-spot black ball
				this.respotBalls(7);

				// generate a random boolean
				const seq: Boolean = Math.random() < 0.5;

				// and randomly choose a player
				if (seq) {
					result = HitResultEnum.OK;
				}
				else {
					result = HitResultEnum.PLAYER_CHANGE;
				}
			}
			else {
				// we have the winner
				if (this.scores[this.currentTurnPlayerIndex] > this.scores[this.nextTurnPlayerIndex]) { // the current player (winner) potted the black ball
					if (this.scores[this.currentTurnPlayerIndex] >= 100)
						result = HitResultEnum.WIN_GAME_CENTURY;
					else
						result = HitResultEnum.WIN_GAME;
					this.scores[this.currentTurnPlayerIndex]++;
					store.commit(MutationType.GAME_MESSAGE, `RESULT: Player ${this.currentTurnPlayer.playerId} won the game with result ${this.scores[this.currentTurnPlayerIndex]} to ${this.nextTurnPlayer.playerId} score of ${this.scores[this.nextTurnPlayerIndex]}.`);
				}
				else { // the current player (winner) did not pot the black ball
					result = HitResultEnum.LOST_GAME;
					this.scores[this.nextTurnPlayerIndex]++;
					store.commit(MutationType.GAME_MESSAGE, `RESULT: Player ${this.nextTurnPlayer.playerId} won the game with result ${this.scores[this.nextTurnPlayerIndex]} to ${this.currentTurnPlayer.playerId} score of ${this.scores[this.currentTurnPlayerIndex]}.`);
				}
			}
		}

		/** Calculate current ball index */
		if (this.stage == GameStage.FirstStage) {
			if (result == HitResultEnum.OK) { // in first game stage
				// alternate between red and colour balls
				this.currentBallId == 1 ? this.currentBallId = -1 : this.currentBallId = 1; // -1 is for coloured balls
			}
			else if (result == HitResultEnum.PLAYER_CHANGE) {
				// after player change switch current ball to red
				this.currentBallId = 1;
			}
		}
		else if (this.stage == GameStage.SecondStage) {
			if (!noBalls) { // ensure balls are on the table before finding current ball
				// illegal potted colour balls needs to be re-spotted before this
				this.findCurrentBall();
			}
		}
		
		return result;
	}

	private setPlayerIcon(): void {
		if (this.currentBallId == -1) {
			this.currentTurnPlayer.player.ball = this.colorIcon; // set player icon
		}
		else {
			let loc: number = this.currentBallId > 1 ? this.currentBallId : this.currentBallId;
			this.currentTurnPlayer.player.ball = this.balls[loc].ballIcon; // set player icon
			this.currentBallPoints = this.balls[loc].ballPoints; // this is used in the potted balls bar
		}
	}

	/**
	 * This finds the current coloured ball in the second stage of the game.
	 */
	private findCurrentBall(): void {
		for (let i: number = 1; i <= 7; i++) {
			let n = (this.reds) + i;
			if (this.balls[n].onTable) {
				this.currentBallId = n;
				break;
			}
		}
	}

	/**
	 * Re-spot all necessary potted balls.
	 * 
	 * @param first - this is the lowest ball point where the ball should be re-spotted
	 * 7 represents the black ball, 2 represents the yellow ball.
	 */
	private respotBalls(first: number): void {
		for (let i: number = 7; i >= first; i--) {
			let b: Ball = this.balls[(this.reds - 1) + i];
			if (!b.onTable) {
				this.respotBall(b, i - 2); // re-spot any pocketed coloured balls
			}
		}
	}

	private respotBall(b: Ball, slot: number): void {
		let ok: boolean = false;
		let position: Vector2 = this.slots[slot];

		if (!this.isValidPositionToPlaceBall(position)) {
			for (let i: number = 5; i > slot; i--) {
				// try free slots from maximum
				let pos = this.slots[slot];
				if (this.isValidPositionToPlaceBall(pos)) {
					b.show(pos);
					ok = true;
					console.log("ball respotted free slots from maximum", b);
					break;
				}
			}

			if (!ok) {
				// if still nothing try to find position closest to slot on the
				// straight line from slot to top cushion
				for (let i: number = 5; i > slot; i--) {
					let startPos: Vector2 = this.slots[i];
					let pos: Vector2 = this.findClosestPoint(startPos);

					if (pos != new Vector2(0, 0)) {
						b.show(pos);
						ok = true;
						console.log("ball respotted closest to slot on the straight line from slot to top cushion", b);
						break;
					}
				}

				if (!ok) {
					if (b.ballPoints == this.blackBall.ballPoints || b.ballPoints == this.pinkBall.ballPoints) {
						// for pink and black ball try to find place above
						// centre line and as close as possible
						let startPos: Vector2 = this.slots[b.ballPoints - 2].add(new Vector2(0, -balls.diameter));
						let pos = this.findClosestPoint(startPos);
						if (pos === new Vector2(0, 0)) {
							// Can't respot ball
							console.log("ERROR - can't re-spot ball", b.ballPoints);
						} else {
							b.show(pos);
						}
					}
				}
			}
		}
		else {
			b.show(position); // found slot for the ball
		}
	}

	private hasNoReds(): boolean {
		let noReds: boolean = true;

		for (let i: number = 1; i <= this.reds; i++) {
			let b: Ball = this.balls[i];
			if (b.onTable) {
				noReds = false;
				break;
			}
		}

		return noReds;
	}

	private changeGameStage(): void {
		this.stage = GameStage.SecondStage;
		this.isSecondStage = true;
	}

	/**
	 * Validate the position of the cue ball on the table.
	 * 
	 * @param position - the position of the cue ball
	 * @returns Retruns "true" if the cue ball postion is valid and "false" of not
	 */
	public validateCueBallPosition(position: Vector2): boolean {		
		if (position.x > Game.BAULK_LINE_POS) {
			return false
		}
		// The cue ball must be re-postioned inside the semi-circle.
		if (isInsideCircle(Game.BAULK_LINE_POS, 433, 150, position.x, position.y)) {
			return true; 
		}
		return false;
	}

	/**
	 * This handles the change the of the current player if necessary.
	 */
	public continueGame(): void {
		if (this.currentTurnPlayer.player !== undefined && this.hitResult === undefined) {
			this.setPlayerIcon();
			this.nextTurnPlayer.player.ball = this.unknownIcon;
		}

		switch (this.hitResult) {
		case HitResultEnum.PLAYER_CHANGE:
			this.currentPlayerIndex = (this.currentPlayerIndex + 1) % this.players.length;

			// Store the player change in the Vues store.
			store.commit(MutationType.PLAY_TURN,this.players[this.currentPlayerIndex].playerId);

			// Change game state if all the red balls are potted.
			if (this.stage == GameStage.FirstStage) {
				if (this.hasNoReds()) {
					this.changeGameStage();
				}
			}

			this.currentTurnPlayer.player.playing = 'true';
			this.setPlayerIcon();

			this.nextTurnPlayer.player.playing = 'false';
			this.nextTurnPlayer.player.ball = this.unknownIcon; // set player icon

			// Emit the "player change" event to the Vue.js front end.
			gameApplication.fireEvent('PLAYER_CHANGE');

			break;
		case HitResultEnum.OK:
			this.currentTurnPlayer.player.playing = 'true';
			this.setPlayerIcon();

			this.nextTurnPlayer.player.playing = 'false';
			this.nextTurnPlayer.player.ball = this.unknownIcon; // set player icon

			// Store the current player in the Vues store.
			store.commit(MutationType.PLAY_TURN, this.players[this.currentPlayerIndex].playerId);

			// Emit the "Okay" event to the Vue.js front end.
			gameApplication.fireEvent('OK');
		}

		this.players[0].score = this.scores[0];
		this.players[1].score = this.scores[1];

		if (this.isEnablePassback) {
			this.players[this.currentPlayerIndex].enablePassback = true;
			this.players[this.nextTurnPlayerIndex].enablePassback = false;
		} else {
			this.players[0].enablePassback = false;
			this.players[1].enablePassback = false;
		}

		// Store the players updated player details in the Vuex.
		// This updates the status of the players in the front end.
		store.commit(MutationType.PLAYER1, this.players[0]);
    	store.commit(MutationType.PLAYER2, this.players[1]);
	}
}
